import {
  Breadcrumbs,
  Box,
  CircularProgress,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Typography,
} from '@mui/joy';
import { SupplierInformation } from '@/components/Pages/Suppliers/SupplierInformation';
import { BuyTable } from '@/components/Pages/Buys/BuyTable';
import { CreateEditSupplierModal } from '@/components/Pages/Suppliers/CreateEditSupplierModal';
import { createFileRoute, Link, useNavigate, Navigate } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
// import { DeleteSupplierModal } from '@/components/Pages/Suppliers/DeleteSupplierModal';
import { LedgerTable } from '@/components/Pages/Accounts/LedgerTable';
import { useSupplier } from '@/hooks/useSuppliers';
import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/suppliers/$id')({
  component: SupplierDetail,
  head: () => pageTitle('Proveedores'),
  validateSearch: z.object({
    tab: z.number().default(0),
  }),
});

function SupplierDetail() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('SUPPLIERS_READ');
  const canWrite = hasClaim('SUPPLIERS_WRITE');

  const { data: supplier, isLoading } = useSupplier(id);
  usePageTitle('Proveedores', supplier?.name);

  const [selectedTab, setSelectedTab] = useState(tab);

  const handleTabChange = (value: number) => {
    setSelectedTab(value);

    navigate({
      search: prev => ({ ...prev, tab: value }),
    });
  };

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/suppliers' />;
  }

  if (!supplier) {
    return <div>Proveedor no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/suppliers'>Proveedores</Link>
          <Typography>{supplier?.name}</Typography>
        </Breadcrumbs>

        {canWrite && (
          <Stack direction='row' gap={1}>
            <CreateEditSupplierModal supplier={supplier} />
            {/* <DeleteSupplierModal
              id={supplier?.id.toString()}
              name={supplier?.name}
            /> */}
          </Stack>
        )}
      </Stack>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => {
          handleTabChange(Number(value));
        }}
      >
        <TabList>
          <Tab>Información</Tab>
          <Tab>Compras</Tab>
          <Tab>Movimientos</Tab>
          {/* <Tab>Balances</Tab> */}
        </TabList>

        <TabPanel value={0}>
          <SupplierInformation supplierId={supplier.id} />
        </TabPanel>
        <TabPanel value={1}>
          <BuyTable
            showSupplierColumn={false}
            emptyMessage='No hay órdenes de compra para este proveedor'
            errorTitle='Error al cargar las órdenes de compra'
            supplierId={supplier.id}
            showFilters={false}
          />
        </TabPanel>
        <TabPanel value={2}>
          <LedgerTable
            initialFilters={{ supplierId: supplier.id.toString() }}
            showFilters={false}
            emptyMessage='No hay movimientos registrados'
            errorTitle='Error al cargar los movimientos'
          />
        </TabPanel>
        {/* <TabPanel value={3}>TBD</TabPanel> */}
      </Tabs>
    </Box>
  );
}
